// ui/bankModal.js
// Bank modal UI component with BloomHold tab support

import { gameState } from '../game/gameState.js';
import { depositItem, withdrawItem, depositAll, getBankContents, getBankStats } from '../systems/bank.js';
import {
    isBloomholdEligible,
    depositToBloomhold,
    withdrawFromBloomhold,
    depositAllToBloomhold,
    moveBankToBloomhold,
    getBloomholdStats,
    countBankEligibleForBloomhold
} from '../systems/bloomhold.js';
import { getItem, getItemName, getItemIcon, getItemColor, getItemCategory } from '../data/items/index.js';
import { formatNumber } from '../utils/format.js';
import { buildItemInfoTooltip } from './equipmentTooltip.js';
import { BLOOMHOLD_CONFIG, SKILLS } from '../game/constants.js';
import logger from '../utils/logger.js';
import { getCurrentSkillId } from '../core/routeSetup.js';
import { toastWarning, toastSuccess } from '../ui/toast.js';
import { forceRefreshInventory } from './inventoryUI.js';

// Tier colors for equipment UI styling (border glow, name color)
// These are for tier indication, NOT material color matching
const TIER_COLORS = {
    tier1: '#CD853F',   // Tier 1 - peru/tan
    tier2: '#3CB371',   // Tier 2 - medium sea green
    tier3: '#5B9BD5',   // Tier 3 - royal blue
    tier4: '#E85D75',   // Tier 4 - crimson
    tier5: '#A8A8A8',   // Tier 5 - silver
    tier6: '#B088F9'    // Tier 6 - purple
};

/**
 * Get tier color based on combat level requirement
 */
function getTierColor(combatLevel) {
    if (!combatLevel || combatLevel < 15) return TIER_COLORS.tier1;
    if (combatLevel < 30) return TIER_COLORS.tier2;
    if (combatLevel < 45) return TIER_COLORS.tier3;
    if (combatLevel < 60) return TIER_COLORS.tier4;
    if (combatLevel < 75) return TIER_COLORS.tier5;
    return TIER_COLORS.tier6;
}

/**
 * Calculate total sale value of all items in bank + BloomHold
 */
function calculateTotalStorageValue() {
    let totalValue = 0;

    // Bank value
    const bankSlots = gameState.getBankSlots();
    for (const slot of bankSlots) {
        if (!slot.itemId) continue;

        // Coins count directly as gold
        if (slot.itemId === 'coins') {
            totalValue += slot.quantity;
            continue;
        }

        const item = getItem(slot.itemId);
        if (item && item.value) {
            totalValue += item.value * slot.quantity;
        }
    }

    // BloomHold value (if unlocked)
    if (gameState.isBloomholdUnlocked()) {
        const bloomholdSlots = gameState.getBloomholdSlots();
        for (const slot of bloomholdSlots) {
            if (!slot.itemId) continue;

            // Coins count directly as gold (unlikely in BloomHold but handle it)
            if (slot.itemId === 'coins') {
                totalValue += slot.quantity;
                continue;
            }

            const item = getItem(slot.itemId);
            if (item && item.value) {
                totalValue += item.value * slot.quantity;
            }
        }
    }

    return totalValue;
}

/**
 * Build tooltip text for an item, with full stats for equipment/food/potions
 */
function buildItemTooltip(item, itemId, quantity, isLocked) {
    const exactQty = quantity.toLocaleString();
    const lockHint = isLocked ? ' (Locked)' : '';

    // Use the comprehensive tooltip builder for all item types
    const baseTooltip = buildItemInfoTooltip(itemId);

    // Add quantity and lock status
    return `${baseTooltip}\n\nQuantity: ${exactQty}${lockHint}`;
}

let modalElement = null;
let inventoryFilter = '';
let bankFilter = '';
let bloomholdFilter = '';
let currentTab = 'bank'; // 'bank' | 'bloomhold'

// Drag state for bank/bloomhold/inventory reorganization
let bankDragSourceIndex = null;
let bloomholdDragSourceIndex = null;
let inventoryDragSourceIndex = null;

// Quantity selector state (persisted via localStorage)
const QTY_KEY = 'idleRealms_bankQty';
const savedQty = localStorage.getItem(QTY_KEY);
let selectedQuantity = savedQty === 'all' || savedQty === 'all-1'
    ? savedQty : (savedQty ? (parseInt(savedQty, 10) || 1) : 1);

// Sort state (persisted via localStorage)
const BANK_SORT_KEY = 'idleRealms_bankSort';
const BLOOMHOLD_SORT_KEY = 'idleRealms_bloomholdSort';
const INV_SORT_KEY = 'idleRealms_invSort';
let bankSort = localStorage.getItem(BANK_SORT_KEY) || 'default';
let bloomholdSort = localStorage.getItem(BLOOMHOLD_SORT_KEY) || 'default';
let inventorySort = localStorage.getItem(INV_SORT_KEY) || 'default';

// Sort settings state (persisted via localStorage)
const SORT_DIR_KEY = 'idleRealms_sortDirections';
const CAT_ORDER_KEY = 'idleRealms_categoryOrder';

const DEFAULT_DIRECTIONS = { name: 'asc', value: 'desc', quantity: 'desc' };
const DEFAULT_CATEGORY_ORDER = ['ores','bars','gems','logs','fish','seeds','crops','herbs','potions','arrows','bows','magic','weapons','armor','materials','pets','other'];

const CATEGORY_GROUPS = [
    { id: 'ores', label: 'Ores', categories: ['ore'] },
    { id: 'bars', label: 'Bars', categories: ['bar'] },
    { id: 'gems', label: 'Gems & Jewelry', categories: ['gem', 'band', 'pendant', 'combat_pendant'] },
    { id: 'logs', label: 'Logs', categories: ['log'] },
    { id: 'fish', label: 'Fish', categories: ['fish_raw', 'fish_cooked', 'fish_empowered'] },
    { id: 'seeds', label: 'Seeds', categories: ['seed', 'tree_seed', 'herb_seed'] },
    { id: 'crops', label: 'Crops', categories: ['crop'] },
    { id: 'herbs', label: 'Herbs', categories: ['herb_grimy', 'herb_clean'] },
    { id: 'potions', label: 'Potions & Vials', categories: ['vial', 'potion'] },
    { id: 'arrows', label: 'Arrows & Ammo', categories: ['arrowhead', 'arrow_shaft', 'arrow'] },
    { id: 'bows', label: 'Bows & Quivers', categories: ['bow', 'quiver'] },
    { id: 'magic', label: 'Magic', categories: ['ichor', 'glyph', 'glyph_core', 'focus'] },
    { id: 'weapons', label: 'Weapons', categories: ['weapon'] },
    { id: 'armor', label: 'Armor', categories: ['armor', 'bulwark', 'set_armor', 'boss_gear'] },
    { id: 'materials', label: 'Hides & Cloth', categories: ['hide', 'cloth', 'bone'] },
    { id: 'pets', label: 'Pets', categories: ['pet_food', 'pet_material'] },
    { id: 'other', label: 'Other', categories: ['currency', 'misc', 'insight'] },
];

let sortDirections = JSON.parse(localStorage.getItem(SORT_DIR_KEY)) || { ...DEFAULT_DIRECTIONS };
let categoryOrder = JSON.parse(localStorage.getItem(CAT_ORDER_KEY)) || [...DEFAULT_CATEGORY_ORDER];
let sortSettingsOpen = false;

// Sort option definitions (dynamic based on sort directions)
function getSortOptions() {
    return [
        { value: 'default', label: 'Default' },
        { value: 'name', label: sortDirections.name === 'desc' ? 'Name (Z - A)' : 'Name (A - Z)' },
        { value: 'quantity', label: sortDirections.quantity === 'asc' ? 'Qty (Low - High)' : 'Qty (High - Low)' },
        { value: 'value', label: sortDirections.value === 'asc' ? 'Value (Low - High)' : 'Value (High - Low)' },
        { value: 'category', label: 'Category' },
    ];
}

function getSortLabel(value) {
    return getSortOptions().find(o => o.value === value)?.label || 'Default';
}

/**
 * Format quantity for display
 */
function formatQuantity(quantity) {
    if (quantity >= 10000) {
        return formatNumber(quantity);
    }
    return quantity.toLocaleString();
}

/**
 * Get the category group index for an item category using the custom order
 */
function getCategoryGroupIndex(category) {
    for (let i = 0; i < categoryOrder.length; i++) {
        const group = CATEGORY_GROUPS.find(g => g.id === categoryOrder[i]);
        if (group && group.categories.includes(category)) return i;
    }
    return categoryOrder.length; // Unknown categories go to end
}

/**
 * Sort comparator for slot objects
 */
function slotComparator(sortKey, a, b) {
    if (!a || !a.itemId) return 1;
    if (!b || !b.itemId) return -1;

    switch (sortKey) {
        case 'name': {
            const cmp = getItemName(a.itemId).localeCompare(getItemName(b.itemId));
            return sortDirections.name === 'desc' ? -cmp : cmp;
        }
        case 'quantity': {
            const cmp = (b.quantity || 0) - (a.quantity || 0);
            return sortDirections.quantity === 'asc' ? -cmp : cmp;
        }
        case 'value': {
            const aVal = getItem(a.itemId)?.value || 0;
            const bVal = getItem(b.itemId)?.value || 0;
            const cmp = bVal - aVal;
            return sortDirections.value === 'asc' ? -cmp : cmp;
        }
        case 'category': {
            const aIdx = getCategoryGroupIndex(getItemCategory(a.itemId));
            const bIdx = getCategoryGroupIndex(getItemCategory(b.itemId));
            if (aIdx !== bIdx) return aIdx - bIdx;
            return getItemName(a.itemId).localeCompare(getItemName(b.itemId));
        }
        default:
            return 0;
    }
}

/**
 * Physically reorder a gameState slot array in-place
 * This mutates the actual data so drag-drop continues to work naturally
 */
function physicallyReorderSlots(slots, sortKey) {
    if (sortKey === 'default') return;
    slots.sort((a, b) => slotComparator(sortKey, a, b));
}

/**
 * Physically reorder inventory slots (fixed 28-slot array with nulls)
 * Filled slots sorted to front, nulls pushed to end
 */
function physicallyReorderInventory(sortKey) {
    if (sortKey === 'default') return;
    const slots = gameState.getInventorySlots();
    const filled = slots.filter(s => s !== null);
    const emptyCount = slots.length - filled.length;
    filled.sort((a, b) => slotComparator(sortKey, a, b));
    for (let i = 0; i < slots.length; i++) {
        slots[i] = i < filled.length ? filled[i] : null;
    }
}

/**
 * Render a sort dropdown
 * @param {string} current - Current sort key
 * @param {string} handler - Window.bankModal handler name
 */
function renderSortDropdown(current, handler) {
    const sortOptions = getSortOptions();
    // Use a blank placeholder as first option so re-selecting the same sort always triggers onchange
    const optionsHTML = `<option value="" disabled selected hidden>${getSortLabel(current)}</option>` +
        sortOptions.map(opt => `<option value="${opt.value}">${opt.label}</option>`).join('');

    return `
        <div class="bank-sort">
            <span class="bank-sort-label">Sort:</span>
            <select class="bank-sort-select" onchange="window.bankModal.${handler}(this.value)">
                ${optionsHTML}
            </select>
            <button class="bank-sort-settings-btn" onclick="window.bankModal.toggleSortSettings()" data-tooltip="Sort Settings">\u2699</button>
        </div>
    `;
}

/**
 * Render the quantity selector (inline version for controls bar)
 */
function renderQuantitySelectorInline() {
    const presets = [1, 5, 10, 50, 100];

    const presetButtons = presets.map(preset => {
        const isActive = typeof selectedQuantity === 'number' && selectedQuantity === preset;
        return `<button class="bank-qty-btn ${isActive ? 'active' : ''}"
                        onclick="window.bankModal.setQuantity('${preset}')">${preset}</button>`;
    }).join('');

    return `
        <div class="bank-quantity-selector">
            <span class="bank-qty-label">Qty:</span>
            <div class="bank-qty-presets">
                ${presetButtons}
                <button class="bank-qty-btn ${selectedQuantity === 'all' ? 'active' : ''}"
                        onclick="window.bankModal.setQuantity('all')">All</button>
                <button class="bank-qty-btn ${selectedQuantity === 'all-1' ? 'active' : ''}"
                        onclick="window.bankModal.setQuantity('all-1')">All-1</button>
            </div>
            <div class="bank-qty-custom">
                <input type="number" class="bank-qty-input"
                       min="1"
                       placeholder="X"
                       value="${typeof selectedQuantity === 'number' && ![1, 5, 10, 50, 100].includes(selectedQuantity) ? selectedQuantity : ''}"
                       onclick="event.stopPropagation()">
                <button class="bank-qty-set-btn" onclick="window.bankModal.setCustomQuantity()">Set</button>
            </div>
        </div>
    `;
}

/**
 * Render the footer with deposit button and hints
 */
function renderFooter() {
    const depositBtnText = currentTab === 'bloomhold' ? 'Deposit Seeds/Herbs' : 'Deposit All';

    return `
        <div class="bank-footer">
            <button class="bank-deposit-all-btn" onclick="window.bankModal.depositAll()">${depositBtnText}</button>
            <span class="bank-lock-hint">Right-click inventory items to lock/unlock</span>
        </div>
    `;
}

/**
 * Set the selected quantity preset
 */
export function setQuantity(preset) {
    if (preset === 'all' || preset === 'all-1') {
        selectedQuantity = preset;
    } else {
        selectedQuantity = parseInt(preset, 10);
    }
    localStorage.setItem(QTY_KEY, String(selectedQuantity));
    updateQuantitySelector();
}

/**
 * Set custom quantity from the input field
 */
export function setCustomQuantity() {
    const input = modalElement?.querySelector('.bank-qty-input');
    if (!input) return;
    const qty = parseInt(input.value, 10);
    if (qty > 0) {
        selectedQuantity = qty;
        localStorage.setItem(QTY_KEY, String(qty));
        updateQuantitySelector();
    }
}

/**
 * Update the quantity selector UI
 */
function updateQuantitySelector() {
    const selector = modalElement?.querySelector('.bank-quantity-selector');
    if (selector) {
        selector.outerHTML = renderQuantitySelectorInline();
        // Re-attach Enter key listener after replacing HTML
        initBankQtyInput();
    }
}

/**
 * Get the current transfer quantity
 */
function getTransferQuantity(maxQuantity) {
    if (selectedQuantity === 'all') {
        return maxQuantity;
    }
    if (selectedQuantity === 'all-1') {
        return Math.max(1, maxQuantity - 1);
    }
    return Math.min(selectedQuantity, maxQuantity);
}

/**
 * Render a single bank/inventory/bloomhold slot
 * @param {object|null} item - { itemId, quantity } or null
 * @param {string} source - 'bank', 'inventory', or 'bloomhold'
 * @param {number} slotIndex - Index in the array for drag operations
 */
function renderSlot(slotItem, source, slotIndex = 0) {
    if (!slotItem) {
        // Empty slot
        return `<div class="bank-slot empty" data-source="${source}" data-slot="${slotIndex}"><div class="bank-slot-content"></div></div>`;
    }

    const { itemId, quantity } = slotItem;

    // Check lock status for inventory items only
    const isLocked = source === 'inventory' && gameState.isItemLocked(itemId);
    const lockedClass = isLocked ? ' locked' : '';
    const lockIcon = isLocked ? '<span class="lock-icon">🔒</span>' : '';

    const item = getItem(itemId);
    const icon = getItemIcon(itemId, quantity);
    const color = getItemColor(itemId);

    // Bank, BloomHold, and Inventory items can be dragged to reorder
    const draggable = (source === 'bank' || source === 'bloomhold' || source === 'inventory') ? 'draggable="true"' : '';
    const dataSlot = `data-slot="${slotIndex}"`;

    // Build tooltip with colored name for equipment
    const tooltip = buildItemTooltip(item, itemId, quantity, isLocked);

    return `
        <div class="bank-slot${lockedClass}"
             data-item-id="${itemId}"
             data-source="${source}"
             ${dataSlot}
             ${draggable}
             data-tooltip="${tooltip}"
             onclick="window.bankModal.handleSlotClick('${itemId}', '${source}', event)">
            <div class="bank-slot-content" style="background-color: ${color};">
                <span class="bank-item-icon">${icon}</span>
                <span class="bank-item-quantity">${formatQuantity(quantity)}</span>
            </div>
            ${lockIcon}
        </div>
    `;
}

/**
 * Render tab navigation
 */
function renderTabNavigation() {
    const bloomholdUnlocked = gameState.isBloomholdUnlocked();
    const farmingLevel = gameState.getSkill(SKILLS.FARMING).level;
    const canPurchase = farmingLevel >= BLOOMHOLD_CONFIG.UNLOCK_LEVEL;

    return `
        <div class="bank-tabs">
            <button class="bank-tab ${currentTab === 'bank' ? 'active' : ''}"
                    onclick="window.bankModal.switchTab('bank')">
                🏦 Bank
            </button>
            <button class="bank-tab ${currentTab === 'bloomhold' ? 'active' : ''} ${!bloomholdUnlocked && !canPurchase ? 'locked' : ''}"
                    onclick="window.bankModal.switchTab('bloomhold')"
                    ${!bloomholdUnlocked && !canPurchase ? 'disabled' : ''}>
                🌱 BloomHold
                ${!bloomholdUnlocked ? '<span class="tab-lock">🔒</span>' : ''}
            </button>
        </div>
    `;
}

/**
 * Render BloomHold unlock panel (when not purchased)
 */
function renderBloomholdUnlockPanel() {
    const farmingLevel = gameState.getSkill(SKILLS.FARMING).level;
    const goldCount = gameState.getItemCount('coins');
    const hasGreenhouse = gameState.hasMilestone('greenhouse');
    const canAfford = goldCount >= BLOOMHOLD_CONFIG.UNLOCK_PRICE;
    const meetsLevel = farmingLevel >= BLOOMHOLD_CONFIG.UNLOCK_LEVEL;
    const canPurchase = hasGreenhouse && meetsLevel && canAfford;

    return `
        <div class="bloomhold-unlock-panel">
            <div class="unlock-icon">🌱</div>
            <h3>BloomHold</h3>
            <p class="unlock-desc">A specialized vault for seeds, crops, and herbs.</p>

            <div class="unlock-requirements">
                <h4>Unlock Requirements</h4>
                <div class="requirement ${hasGreenhouse ? 'met' : 'unmet'}">
                    <span class="req-icon">${hasGreenhouse ? '✓' : '✗'}</span>
                    Build Greenhouse (Architecting Lv35)
                    <span class="req-status">${hasGreenhouse ? 'Built' : 'Not Built'}</span>
                </div>
                <div class="requirement ${meetsLevel ? 'met' : 'unmet'}">
                    <span class="req-icon">${meetsLevel ? '✓' : '✗'}</span>
                    Level ${BLOOMHOLD_CONFIG.UNLOCK_LEVEL} Farming
                    (Current: ${farmingLevel})
                </div>
                <div class="requirement ${canAfford ? 'met' : 'unmet'}">
                    <span class="req-icon">${canAfford ? '✓' : '✗'}</span>
                    ${formatNumber(BLOOMHOLD_CONFIG.UNLOCK_PRICE)} Gold
                    (Have: ${formatNumber(goldCount)})
                </div>
            </div>

            <button class="unlock-button ${canPurchase ? '' : 'disabled'}"
                    onclick="window.bankModal.purchaseBloomhold()"
                    ${canPurchase ? '' : 'disabled'}>
                Purchase BloomHold
            </button>

            <p class="unlock-features">
                <strong>Features:</strong><br>
                • ${BLOOMHOLD_CONFIG.MAX_SLOTS} dedicated storage slots<br>
                • Plant directly from BloomHold<br>
                • Use herbs for Alchemy from BloomHold<br>
                • Auto-harvest to BloomHold option
            </p>
        </div>
    `;
}

/**
 * Render "Move to BloomHold" button (only when bank has eligible items)
 */
function renderMoveToBloomholdButton() {
    if (!gameState.isBloomholdUnlocked()) return '';
    if (currentTab !== 'bank') return '';

    const { count, quantity } = countBankEligibleForBloomhold();
    if (count === 0) return '';

    return `
        <button class="move-to-bloomhold-btn" onclick="window.bankModal.moveToBloomhold()">
            🌱 Move ${count} item${count > 1 ? 's' : ''} to BloomHold (${formatNumber(quantity)} total)
        </button>
    `;
}

/**
 * Render bank panel (grid only)
 * Uses raw slot indices for drag-and-drop when not filtering
 */
function renderBankPanel() {
    // Get raw bank slots with their actual indices
    const rawSlots = gameState.getBankSlots();

    // Apply filter if needed
    let displaySlots;
    if (bankFilter) {
        const lowerFilter = bankFilter.toLowerCase();
        displaySlots = rawSlots
            .map((slot, index) => ({ ...slot, originalIndex: index }))
            .filter(slot => {
                const name = getItemName(slot.itemId).toLowerCase();
                return name.includes(lowerFilter);
            });
    } else {
        displaySlots = rawSlots.map((slot, index) => ({ ...slot, originalIndex: index }));
    }

    const slotsHTML = displaySlots.map(slot =>
        renderSlot(slot, 'bank', slot.originalIndex)
    ).join('');

    return `
        <div class="bank-panel bank">
            <div class="bank-grid" id="bank-grid">
                ${slotsHTML || '<div class="bank-empty">Bank is empty</div>'}
            </div>
        </div>
    `;
}

/**
 * Render BloomHold panel (grid)
 */
function renderBloomholdPanel() {
    const rawSlots = gameState.getBloomholdSlots();

    // Apply filter if needed
    let displaySlots;
    if (bloomholdFilter) {
        const lowerFilter = bloomholdFilter.toLowerCase();
        displaySlots = rawSlots
            .map((slot, index) => ({ ...slot, originalIndex: index }))
            .filter(slot => {
                const name = getItemName(slot.itemId).toLowerCase();
                return name.includes(lowerFilter);
            });
    } else {
        displaySlots = rawSlots.map((slot, index) => ({ ...slot, originalIndex: index }));
    }

    const slotsHTML = displaySlots.map(slot =>
        renderSlot(slot, 'bloomhold', slot.originalIndex)
    ).join('');

    return `
        <div class="bank-panel bloomhold">
            <div class="bank-grid" id="bloomhold-grid">
                ${slotsHTML || '<div class="bank-empty">BloomHold is empty</div>'}
            </div>
        </div>
    `;
}

/**
 * Create the bank modal HTML - with tabs for Bank and BloomHold
 * Uses side-by-side layout: Bank/BloomHold on left, Inventory on right
 */
function createModalHTML() {
    const bankStats = getBankStats();
    const bloomholdStats = getBloomholdStats();
    const bloomholdUnlocked = gameState.isBloomholdUnlocked();

    let headerText;
    let mainPanelContent;
    let searchPlaceholder;
    let searchValue;
    let searchHandler;
    let extraControls = '';

    if (currentTab === 'bank') {
        headerText = `Bank <span class="bank-header-count">(${bankStats.usedSlots}/${bankStats.maxSlots})</span>`;
        searchPlaceholder = 'Search bank...';
        searchValue = bankFilter;
        searchHandler = 'setBankFilter';
        mainPanelContent = `
            ${renderMoveToBloomholdButton()}
            ${renderBankPanel()}
        `;
    } else {
        // BloomHold tab
        if (bloomholdUnlocked) {
            headerText = `BloomHold <span class="bank-header-count">(${bloomholdStats.usedSlots}/${bloomholdStats.maxSlots})</span>`;
            searchPlaceholder = 'Search BloomHold...';
            searchValue = bloomholdFilter;
            searchHandler = 'setBloomholdFilter';
            const autoDepositEnabled = gameState.isBloomholdAutoDepositEnabled();
            extraControls = `
                <label class="bloomhold-toggle-label" style="margin-left: auto;">
                    <input type="checkbox"
                           class="bloomhold-toggle"
                           ${autoDepositEnabled ? 'checked' : ''}
                           onchange="window.bankModal.toggleAutoDeposit(this.checked)">
                    Auto-deposit
                </label>
            `;
            mainPanelContent = renderBloomholdPanel();
        } else {
            headerText = 'BloomHold';
            // Special case: show unlock panel full width
            return `
                <div class="modal-overlay" id="bank-modal-overlay">
                    <div class="modal-content bank-modal">
                        <div class="modal-header">
                            <h2>${headerText}</h2>
                            <button class="modal-close" onclick="window.bankModal.close()">&times;</button>
                        </div>
                        <div class="modal-body">
                            ${renderTabNavigation()}
                            ${renderBloomholdUnlockPanel()}
                        </div>
                    </div>
                </div>
            `;
        }
    }

    const usedSlots = gameState.getUniqueItemCount();

    const totalStorageValue = calculateTotalStorageValue();

    return `
        <div class="modal-overlay" id="bank-modal-overlay">
            <div class="modal-content bank-modal">
                <div class="modal-header">
                    <h2>${headerText}</h2>
                    <span class="bank-value"><span class="bank-value-icon">${getItemIcon('coins', totalStorageValue)}</span> Bank Value: ${totalStorageValue.toLocaleString()} gold</span>
                    <button class="modal-close" onclick="window.bankModal.close()">&times;</button>
                </div>
                <div class="modal-body">
                    <!-- Controls Bar: Tabs + Search + Sort + Quantity -->
                    <div class="bank-controls-bar">
                        ${renderTabNavigation()}
                        <input type="text"
                               class="bank-search"
                               placeholder="${searchPlaceholder}"
                               value="${searchValue}"
                               oninput="window.bankModal.${searchHandler}(this.value)">
                        ${currentTab === 'bloomhold' && bloomholdUnlocked ? renderSortDropdown(bloomholdSort, 'setBloomholdSort') : renderSortDropdown(bankSort, 'setBankSort')}
                        ${renderQuantitySelectorInline()}
                        ${extraControls}
                    </div>

                    <!-- Side-by-side panels -->
                    <div class="bank-split-panels">
                        <div class="bank-main-panel">
                            ${mainPanelContent}
                        </div>
                        <div class="bank-inventory-panel">
                            <div class="bank-panel-header">
                                <span class="bank-panel-title">Inventory</span>
                                <span class="bank-panel-count">${usedSlots}/28</span>
                                ${renderSortDropdown(inventorySort, 'setInventorySort')}
                            </div>
                            ${renderInventoryGridOnly()}
                        </div>
                    </div>

                    ${renderFooter()}
                </div>
            </div>
        </div>
    `;
}

/**
 * Render just the inventory grid (no wrapper)
 */
function renderInventoryGridOnly() {
    const slots = gameState.getInventorySlots();

    let filteredSlots = slots;
    if (inventoryFilter) {
        const lowerFilter = inventoryFilter.toLowerCase();
        filteredSlots = slots.map(slot => {
            if (!slot) return null;
            const name = getItemName(slot.itemId).toLowerCase();
            return name.includes(lowerFilter) ? slot : null;
        });
    }

    const slotsHTML = filteredSlots.map((slot, index) => renderSlot(slot, 'inventory', index)).join('');

    return `
        <div class="bank-grid">
            ${slotsHTML || '<div class="bank-empty">Empty</div>'}
        </div>
    `;
}

/**
 * Open the bank modal
 */
export function openBankModal() {
    closeBankModal();

    // Reset filters and settings panel but keep tab
    inventoryFilter = '';
    bankFilter = '';
    bloomholdFilter = '';
    sortSettingsOpen = false;

    const modalContainer = document.createElement('div');
    modalContainer.innerHTML = createModalHTML();
    modalElement = modalContainer.firstElementChild;
    document.body.appendChild(modalElement);

    // Close on overlay click
    modalElement.addEventListener('click', (e) => {
        if (e.target === modalElement) {
            closeBankModal();
        }
    });

    // Close on Escape key
    document.addEventListener('keydown', handleEscapeKey);

    // Enter key handler for bank custom qty input
    initBankQtyInput();

    // Initialize drag and drop for current tab
    initDragDrop();

    logger.debug('[BANK] Modal opened');
}

/**
 * Close the bank modal
 */
export function closeBankModal() {
    if (modalElement) {
        // Clean up drag state before removing modal (prevents stuck body.dragging)
        cleanupDragState();

        modalElement.remove();
        modalElement = null;
        document.removeEventListener('keydown', handleEscapeKey);
        logger.debug('[BANK] Modal closed');

        // Refresh farming UI if on farming page (BloomHold data may have changed)
        if (getCurrentSkillId() === SKILLS.FARMING) {
            import('../skills/farming/farmingUI.js').then(({ renderFarmingUI }) => {
                renderFarmingUI();
            });
        }
    }
}

/**
 * Handle Escape key
 */
function handleEscapeKey(e) {
    if (e.key === 'Escape') {
        closeBankModal();
    }
}

/**
 * Switch between Bank and BloomHold tabs
 */
export function switchTab(tab) {
    // Carry search text to the new tab
    if (currentTab === 'bank' && tab === 'bloomhold') {
        bloomholdFilter = bankFilter;
    } else if (currentTab === 'bloomhold' && tab === 'bank') {
        bankFilter = bloomholdFilter;
    }
    currentTab = tab;

    if (!modalElement) return;

    // Only update the modal-content, not the overlay (prevents flicker)
    const modalContent = modalElement.querySelector('.modal-content');
    if (modalContent) {
        // Create new HTML and extract just the modal-content
        const container = document.createElement('div');
        container.innerHTML = createModalHTML();
        const newModalContent = container.querySelector('.modal-content');

        if (newModalContent) {
            modalContent.innerHTML = newModalContent.innerHTML;
        }

        // Re-initialize drag and drop and input listener
        initDragDrop();
        initBankQtyInput();
    }
}

/**
 * Update the modal display
 * Only updates grid contents and counts, preserving search inputs to maintain focus
 */
function updateModalDisplay() {
    if (!modalElement) return;

    // Update inventory grid (new structure: .bank-inventory-panel .bank-grid)
    const inventoryGrid = modalElement.querySelector('.bank-inventory-panel .bank-grid') ||
                          modalElement.querySelector('.bank-panel.inventory .bank-grid');
    if (inventoryGrid) {
        const slots = gameState.getInventorySlots();
        let filteredSlots = slots;
        if (inventoryFilter) {
            const lowerFilter = inventoryFilter.toLowerCase();
            filteredSlots = slots.map(slot => {
                if (!slot) return null;
                const name = getItemName(slot.itemId).toLowerCase();
                return name.includes(lowerFilter) ? slot : null;
            });
        }
        const slotsHTML = filteredSlots.map((slot, index) => renderSlot(slot, 'inventory', index)).join('');
        inventoryGrid.innerHTML = slotsHTML || '<div class="bank-empty">Empty</div>';
    }

    // Update bank or bloomhold grid based on current tab
    if (currentTab === 'bank') {
        const bankGrid = modalElement.querySelector('.bank-panel.bank .bank-grid');
        if (bankGrid) {
            const rawSlots = gameState.getBankSlots();
            let displaySlots;
            if (bankFilter) {
                const lowerFilter = bankFilter.toLowerCase();
                displaySlots = rawSlots
                    .map((slot, index) => ({ ...slot, originalIndex: index }))
                    .filter(slot => {
                        const name = getItemName(slot.itemId).toLowerCase();
                        return name.includes(lowerFilter);
                    });
            } else {
                displaySlots = rawSlots.map((slot, index) => ({ ...slot, originalIndex: index }));
            }
            const slotsHTML = displaySlots.map(slot =>
                renderSlot(slot, 'bank', slot.originalIndex)
            ).join('');
            bankGrid.innerHTML = slotsHTML || '<div class="bank-empty">Bank is empty</div>';
        }

        // Update move to bloomhold button
        const moveBtn = modalElement.querySelector('.move-to-bloomhold-btn');
        if (moveBtn) {
            const { count, quantity } = countBankEligibleForBloomhold();
            if (count === 0) {
                moveBtn.remove();
            } else {
                moveBtn.textContent = `🌱 Move ${count} item${count > 1 ? 's' : ''} to BloomHold (${formatNumber(quantity)} total)`;
            }
        } else if (gameState.isBloomholdUnlocked()) {
            // Button might need to be added
            const { count } = countBankEligibleForBloomhold();
            if (count > 0) {
                const bankMain = modalElement.querySelector('.bank-main');
                const searchInput = bankMain?.querySelector('.bank-search');
                if (searchInput && bankMain) {
                    searchInput.insertAdjacentHTML('afterend', renderMoveToBloomholdButton());
                }
            }
        }

        // Update bank header count
        const bankHeaderCount = modalElement.querySelector('.bank-header-count');
        if (bankHeaderCount) {
            const stats = getBankStats();
            bankHeaderCount.textContent = `(${stats.usedSlots}/${stats.maxSlots})`;
        }
    } else if (currentTab === 'bloomhold' && gameState.isBloomholdUnlocked()) {
        const bloomholdGrid = modalElement.querySelector('.bank-panel.bloomhold .bank-grid');
        if (bloomholdGrid) {
            const rawSlots = gameState.getBloomholdSlots();
            let displaySlots;
            if (bloomholdFilter) {
                const lowerFilter = bloomholdFilter.toLowerCase();
                displaySlots = rawSlots
                    .map((slot, index) => ({ ...slot, originalIndex: index }))
                    .filter(slot => {
                        const name = getItemName(slot.itemId).toLowerCase();
                        return name.includes(lowerFilter);
                    });
            } else {
                displaySlots = rawSlots.map((slot, index) => ({ ...slot, originalIndex: index }));
            }
            const slotsHTML = displaySlots.map(slot =>
                renderSlot(slot, 'bloomhold', slot.originalIndex)
            ).join('');
            bloomholdGrid.innerHTML = slotsHTML || '<div class="bank-empty">BloomHold is empty</div>';
        }

        // Update bloomhold header count
        const headerCount = modalElement.querySelector('.bank-header-count');
        if (headerCount) {
            const stats = getBloomholdStats();
            headerCount.textContent = `(${stats.usedSlots}/${stats.maxSlots})`;
        }
    }

    // Update inventory count (new structure: .bank-inventory-panel .bank-panel-count)
    const invCount = modalElement.querySelector('.bank-inventory-panel .bank-panel-count') ||
                     modalElement.querySelector('.bank-panel.inventory .bank-panel-count');
    if (invCount) {
        invCount.textContent = `${gameState.getUniqueItemCount()}/28`;
    }

    // Update bank value display
    const bankValueEl = modalElement.querySelector('.bank-value');
    if (bankValueEl) {
        const totalValue = calculateTotalStorageValue();
        const iconEl = bankValueEl.querySelector('.bank-value-icon');
        if (iconEl) {
            iconEl.innerHTML = getItemIcon('coins', totalValue);
        }
        // Update text node after icon
        const textNode = bankValueEl.childNodes[bankValueEl.childNodes.length - 1];
        if (textNode && textNode.nodeType === Node.TEXT_NODE) {
            textNode.textContent = ` Bank Value: ${totalValue.toLocaleString()} gold`;
        }
    }
}

/**
 * Handle slot click (transfer item)
 */
export function handleSlotClick(itemId, source, event) {
    // Get the max quantity available from the source
    let maxQuantity = 0;
    if (source === 'inventory') {
        maxQuantity = gameState.getItemCount(itemId);
    } else if (source === 'bank') {
        maxQuantity = gameState.getBankItemCount(itemId);
    } else if (source === 'bloomhold') {
        maxQuantity = gameState.getBloomholdItemCount(itemId);
    }

    // Calculate transfer quantity (null means all for the API)
    const transferQty = getTransferQuantity(maxQuantity);
    const quantity = selectedQuantity === 'all' ? null : transferQty;

    if (source === 'inventory') {
        if (currentTab === 'bloomhold' && gameState.isBloomholdUnlocked()) {
            // On BloomHold tab: deposit eligible items to BloomHold
            if (isBloomholdEligible(itemId)) {
                const result = depositToBloomhold(itemId, quantity);
                if (!result.success) {
                    logger.debug(`[BLOOMHOLD] Deposit failed: ${result.reason}`);
                }
            } else {
                // Not eligible - deposit to regular bank
                const result = depositItem(itemId, quantity);
                if (!result.success) {
                    logger.debug(`[BANK] Deposit failed: ${result.reason}`);
                }
            }
        } else {
            // On Bank tab: deposit to regular bank
            const result = depositItem(itemId, quantity);
            if (!result.success) {
                logger.debug(`[BANK] Deposit failed: ${result.reason}`);
            }
        }
    } else if (source === 'bank') {
        const result = withdrawItem(itemId, quantity);
        if (!result.success) {
            logger.debug(`[BANK] Withdraw failed: ${result.reason}`);
        }
    } else if (source === 'bloomhold') {
        const result = withdrawFromBloomhold(itemId, quantity);
        if (!result.success) {
            logger.debug(`[BLOOMHOLD] Withdraw failed: ${result.reason}`);
        }
    }

    updateModalDisplay();
}

/**
 * Deposit all items from inventory
 */
export function handleDepositAll() {
    if (currentTab === 'bloomhold' && gameState.isBloomholdUnlocked()) {
        // On BloomHold tab: deposit eligible items to BloomHold
        depositAllToBloomhold();
    } else {
        // On Bank tab: check auto-deposit setting
        if (gameState.isBloomholdUnlocked() && gameState.isBloomholdAutoDepositEnabled()) {
            // Auto-deposit: send eligible items to BloomHold first, then rest to bank
            depositAllToBloomhold();
        }
        // Deposit remaining items to bank
        depositAll();
    }
    updateModalDisplay();
}

/**
 * Move eligible items from bank to BloomHold
 */
export function handleMoveToBloomhold() {
    const result = moveBankToBloomhold();
    const movedCount = Object.keys(result.moved).length;
    const failedCount = Object.keys(result.failed).length;

    if (movedCount > 0 && failedCount === 0) {
        toastSuccess(`Moved ${movedCount} item type${movedCount > 1 ? 's' : ''} to BloomHold`);
    } else if (movedCount > 0 && failedCount > 0) {
        toastWarning(`Moved ${movedCount} item type${movedCount > 1 ? 's' : ''}, but BloomHold is full (${failedCount} couldn't fit)`);
    } else if (failedCount > 0) {
        toastWarning('BloomHold is full! No items could be moved.');
    }

    updateModalDisplay();
}

/**
 * Purchase BloomHold
 */
export function purchaseBloomhold() {
    const farmingLevel = gameState.getSkill(SKILLS.FARMING).level;
    const result = gameState.purchaseBloomhold(farmingLevel);

    if (result.success) {
        logger.game('BloomHold purchased!');
        // Re-render modal to show BloomHold grid
        switchTab('bloomhold');

        // If on farming page, trigger re-render to update auto-harvest options and seed counts
        if (getCurrentSkillId() === SKILLS.FARMING) {
            import('../skills/farming/farmingUI.js').then(({ renderFarmingUI }) => {
                renderFarmingUI();
            });
        }
    } else {
        logger.debug(`[BLOOMHOLD] Purchase failed: ${result.reason}`);
    }
}

/**
 * Set inventory filter
 */
export function setInventoryFilter(value) {
    inventoryFilter = value;
    updateModalDisplay();
}

/**
 * Set bank filter
 */
export function setBankFilter(value) {
    bankFilter = value;
    updateModalDisplay();
}

/**
 * Set BloomHold filter
 */
export function setBloomholdFilter(value) {
    bloomholdFilter = value;
    updateModalDisplay();
}

/**
 * Toggle auto-deposit to BloomHold setting
 */
export function toggleAutoDeposit(enabled) {
    gameState.setBloomholdAutoDeposit(enabled);
    logger.info(`BloomHold auto-deposit ${enabled ? 'enabled' : 'disabled'}`);
}

/**
 * Sort bank/bloomhold — physically reorder slots then refresh
 */
export function setBankSort(value) {
    bankSort = value;
    localStorage.setItem(BANK_SORT_KEY, value);
    if (value !== 'default') {
        physicallyReorderSlots(gameState.getBankSlots(), value);
    }
    updateModalDisplay();
    // Update placeholder text and reset so the same option can be re-selected
    const select = modalElement?.querySelector('.bank-controls-bar .bank-sort-select');
    if (select) {
        select.options[0].textContent = getSortLabel(value);
        select.selectedIndex = 0;
    }
}

/**
 * Sort BloomHold — physically reorder slots then refresh
 */
export function setBloomholdSort(value) {
    bloomholdSort = value;
    localStorage.setItem(BLOOMHOLD_SORT_KEY, value);
    if (value !== 'default' && gameState.isBloomholdUnlocked()) {
        physicallyReorderSlots(gameState.getBloomholdSlots(), value);
    }
    updateModalDisplay();
    // Update placeholder text and reset so the same option can be re-selected
    const select = modalElement?.querySelector('.bank-controls-bar .bank-sort-select');
    if (select) {
        select.options[0].textContent = getSortLabel(value);
        select.selectedIndex = 0;
    }
}

/**
 * Sort inventory — physically reorder slots then refresh
 */
export function setInventorySort(value) {
    inventorySort = value;
    localStorage.setItem(INV_SORT_KEY, value);
    if (value !== 'default') {
        physicallyReorderInventory(value);
    }
    updateModalDisplay();
    // Also refresh sidebar inventory so it reflects the new order
    forceRefreshInventory();
    // Update placeholder text and reset so the same option can be re-selected
    const select = modalElement?.querySelector('.bank-panel-header .bank-sort-select');
    if (select) {
        select.options[0].textContent = getSortLabel(value);
        select.selectedIndex = 0;
    }
}

/**
 * Initialize Enter key handler for bank custom qty input
 */
function initBankQtyInput() {
    if (!modalElement) return;
    const input = modalElement.querySelector('.bank-qty-input');
    if (input) {
        input.addEventListener('keypress', (e) => {
            if (e.key === 'Enter') {
                setCustomQuantity();
            }
        });
    }
}

// ============================================
// Drag and Drop Handlers
// ============================================

/**
 * Handle drag start on bank/bloomhold/inventory slot
 */
function handleDragStart(e) {
    const slot = e.target.closest('.bank-slot');
    if (!slot || slot.classList.contains('empty')) {
        e.preventDefault();
        return;
    }

    const source = slot.dataset.source;

    // Don't allow dragging when filter is active (inventory filter doesn't exist in bank modal)
    if ((source === 'bank' && bankFilter) || (source === 'bloomhold' && bloomholdFilter)) {
        e.preventDefault();
        return;
    }

    const slotIndex = parseInt(slot.dataset.slot, 10);

    if (source === 'bank') {
        bankDragSourceIndex = slotIndex;
    } else if (source === 'bloomhold') {
        bloomholdDragSourceIndex = slotIndex;
    } else if (source === 'inventory') {
        inventoryDragSourceIndex = slotIndex;
    }

    slot.classList.add('dragging');

    // Hide tooltips during drag
    document.body.classList.add('dragging');

    // Create custom drag image showing just the icon
    const iconEl = slot.querySelector('.bank-item-icon');
    if (iconEl) {
        const dragImage = document.createElement('div');
        dragImage.className = 'drag-image';
        dragImage.innerHTML = iconEl.innerHTML;
        dragImage.style.cssText = `
            position: absolute;
            top: -9999px;
            left: -9999px;
            padding: 6px;
            background: rgba(30, 35, 45, 0.95);
            border: 2px solid #d4af37;
            border-radius: 6px;
            font-size: 1.5rem;
            display: flex;
            align-items: center;
            justify-content: center;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);
        `;
        document.body.appendChild(dragImage);

        // Set as drag image, centered on cursor
        e.dataTransfer.setDragImage(dragImage, dragImage.offsetWidth / 2, dragImage.offsetHeight / 2);

        // Clean up after drag starts
        setTimeout(() => dragImage.remove(), 0);
    }

    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', slotIndex.toString());
    e.dataTransfer.setData(`application/x-${source}-slot`, slotIndex.toString());
    e.dataTransfer.setData('application/x-source', source);

    logger.debug(`[${source.toUpperCase()}] Drag start: slot ${slotIndex}`);
}

// Auto-scroll state
let scrollInterval = null;

/**
 * Handle drag over slot with auto-scroll
 */
function handleDragOver(e) {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';

    const slot = e.target.closest('.bank-slot');
    if (slot) {
        const source = slot.dataset.source;
        // Only allow drop on same source type
        if ((source === 'bank' && bankDragSourceIndex !== null) ||
            (source === 'bloomhold' && bloomholdDragSourceIndex !== null) ||
            (source === 'inventory' && inventoryDragSourceIndex !== null)) {
            slot.classList.add('drag-over');
        }
    }

    // Auto-scroll is handled by handleModalDragOver for better edge detection
}

/**
 * Handle drag over on modal level for auto-scroll
 * This allows scrolling even when cursor is outside the grid
 */
function handleModalDragOver(e) {
    // Only process if we're actively dragging a bank/bloomhold item
    if (bankDragSourceIndex === null && bloomholdDragSourceIndex === null) return;

    // Find the appropriate grid based on what we're dragging
    const gridSelector = bankDragSourceIndex !== null ? '.bank-panel.bank .bank-grid' : '.bank-panel.bloomhold .bank-grid';
    const scrollContainer = modalElement?.querySelector(gridSelector);
    if (!scrollContainer) return;

    const rect = scrollContainer.getBoundingClientRect();
    const scrollSpeed = 10;
    const edgeThreshold = 60; // pixels from grid edge to trigger scroll

    const mouseY = e.clientY;

    // Clear any existing scroll interval
    if (scrollInterval) {
        clearInterval(scrollInterval);
        scrollInterval = null;
    }

    // Scroll up when mouse is near or above the top of the grid
    if (mouseY < rect.top + edgeThreshold && scrollContainer.scrollTop > 0) {
        scrollInterval = setInterval(() => {
            scrollContainer.scrollTop -= scrollSpeed;
        }, 16);
    }
    // Scroll down when mouse is near or below the bottom of the grid
    else if (mouseY > rect.bottom - edgeThreshold &&
             scrollContainer.scrollTop < scrollContainer.scrollHeight - scrollContainer.clientHeight) {
        scrollInterval = setInterval(() => {
            scrollContainer.scrollTop += scrollSpeed;
        }, 16);
    }
}

/**
 * Handle drag leave slot
 */
function handleDragLeave(e) {
    const slot = e.target.closest('.bank-slot');
    if (slot) {
        slot.classList.remove('drag-over');
    }
}

/**
 * Clean up all drag state (body class, scroll interval, drag indices)
 * Called after drop and on modal close to prevent stuck body.dragging
 */
function cleanupDragState() {
    if (scrollInterval) {
        clearInterval(scrollInterval);
        scrollInterval = null;
    }
    document.body.classList.remove('dragging');
    bankDragSourceIndex = null;
    bloomholdDragSourceIndex = null;
    inventoryDragSourceIndex = null;
}

/**
 * Handle drop on slot
 */
function handleDrop(e) {
    e.preventDefault();

    // Clean up drag state BEFORE updateModalDisplay replaces DOM
    // (otherwise dragend can't fire on the removed source element)
    cleanupDragState();

    const targetSlot = e.target.closest('.bank-slot');
    if (!targetSlot) return;

    const targetSource = targetSlot.dataset.source;
    const targetIndex = parseInt(targetSlot.dataset.slot, 10);
    targetSlot.classList.remove('drag-over');

    if (targetSource === 'bank') {
        const sourceData = e.dataTransfer.getData('application/x-bank-slot');
        if (!sourceData) return;

        const sourceIndex = parseInt(sourceData, 10);
        if (sourceIndex === targetIndex) return;

        const result = gameState.moveBankItem(sourceIndex, targetIndex);
        if (result.success) {
            logger.debug(`[BANK] Moved slot ${sourceIndex} to ${targetIndex}`);
            updateModalDisplay();
        }
    } else if (targetSource === 'bloomhold') {
        const sourceData = e.dataTransfer.getData('application/x-bloomhold-slot');
        if (!sourceData) return;

        const sourceIndex = parseInt(sourceData, 10);
        if (sourceIndex === targetIndex) return;

        const result = gameState.moveBloomholdItem(sourceIndex, targetIndex);
        if (result.success) {
            logger.debug(`[BLOOMHOLD] Moved slot ${sourceIndex} to ${targetIndex}`);
            updateModalDisplay();
        }
    } else if (targetSource === 'inventory') {
        const sourceData = e.dataTransfer.getData('application/x-inventory-slot');
        if (!sourceData) return;

        const sourceIndex = parseInt(sourceData, 10);
        if (sourceIndex === targetIndex) return;

        const result = gameState.moveInventoryItem(sourceIndex, targetIndex);
        if (result.success) {
            logger.debug(`[INVENTORY] Moved slot ${sourceIndex} to ${targetIndex}`);
            updateModalDisplay();
        }
    }
}

/**
 * Handle drag end (cleanup)
 */
function handleDragEnd(e) {
    cleanupDragState();

    if (modalElement) {
        const slots = modalElement.querySelectorAll('.bank-slot');
        slots.forEach(slot => {
            slot.classList.remove('dragging', 'drag-over');
        });
    }
}

/**
 * Handle right-click on inventory slot to toggle item lock
 */
function handleInventoryContextMenu(e) {
    const slot = e.target.closest('.bank-slot');
    if (!slot) return;

    // Only handle inventory slots
    if (slot.dataset.source !== 'inventory') return;

    e.preventDefault();

    const itemId = slot.dataset.itemId;
    if (!itemId) return;

    const newLockState = gameState.toggleItemLock(itemId);
    logger.debug(`[BANK] Item ${itemId} ${newLockState ? 'locked' : 'unlocked'}`);

    updateModalDisplay();
    // Also refresh the main inventory display so lock icon shows there too
    forceRefreshInventory();
}

/**
 * Initialize drag and drop listeners for current tab
 */
function initDragDrop() {
    if (!modalElement) return;

    // Modal-level dragover for auto-scroll (works even when cursor is outside grid)
    const modalContent = modalElement.querySelector('.modal-content');
    if (modalContent) {
        modalContent.addEventListener('dragover', handleModalDragOver);
    }

    // Bank grid drag and drop
    const bankGrid = modalElement.querySelector('.bank-panel.bank .bank-grid');
    if (bankGrid) {
        bankGrid.addEventListener('dragstart', handleDragStart);
        bankGrid.addEventListener('dragover', handleDragOver);
        bankGrid.addEventListener('dragleave', handleDragLeave);
        bankGrid.addEventListener('drop', handleDrop);
        bankGrid.addEventListener('dragend', handleDragEnd);
    }

    // BloomHold grid drag and drop
    const bloomholdGrid = modalElement.querySelector('.bank-panel.bloomhold .bank-grid');
    if (bloomholdGrid) {
        bloomholdGrid.addEventListener('dragstart', handleDragStart);
        bloomholdGrid.addEventListener('dragover', handleDragOver);
        bloomholdGrid.addEventListener('dragleave', handleDragLeave);
        bloomholdGrid.addEventListener('drop', handleDrop);
        bloomholdGrid.addEventListener('dragend', handleDragEnd);
    }

    // Inventory grid - drag/drop + right-click lock (new structure: .bank-inventory-panel .bank-grid)
    const inventoryGrid = modalElement.querySelector('.bank-inventory-panel .bank-grid') ||
                          modalElement.querySelector('.bank-panel.inventory .bank-grid');
    if (inventoryGrid) {
        inventoryGrid.addEventListener('dragstart', handleDragStart);
        inventoryGrid.addEventListener('dragover', handleDragOver);
        inventoryGrid.addEventListener('dragleave', handleDragLeave);
        inventoryGrid.addEventListener('drop', handleDrop);
        inventoryGrid.addEventListener('dragend', handleDragEnd);
        inventoryGrid.addEventListener('contextmenu', handleInventoryContextMenu);
    }

    logger.debug('[BANK] Drag and drop initialized');
}

// ============================================
// Sort Settings Panel
// ============================================

/**
 * Render the sort settings panel overlay
 */
function renderSortSettingsPanel() {
    const directionRows = [
        { key: 'name', label: 'Name', asc: 'A - Z', desc: 'Z - A' },
        { key: 'value', label: 'Value', asc: 'Low - High', desc: 'High - Low' },
        { key: 'quantity', label: 'Quantity', asc: 'Low - High', desc: 'High - Low' },
    ];

    const directionHTML = directionRows.map(row => `
        <div class="sort-direction-row">
            <span class="sort-direction-label">${row.label}</span>
            <div class="sort-direction-btns">
                <button class="sort-direction-btn ${sortDirections[row.key] === 'asc' ? 'active' : ''}"
                        onclick="window.bankModal.toggleSortDirection('${row.key}', 'asc')">${row.asc}</button>
                <button class="sort-direction-btn ${sortDirections[row.key] === 'desc' ? 'active' : ''}"
                        onclick="window.bankModal.toggleSortDirection('${row.key}', 'desc')">${row.desc}</button>
            </div>
        </div>
    `).join('');

    const categoryHTML = categoryOrder.map((groupId, index) => {
        const group = CATEGORY_GROUPS.find(g => g.id === groupId);
        if (!group) return '';
        return `
            <div class="sort-category-row" draggable="true" data-group-id="${groupId}">
                <span class="sort-category-handle">\u2630</span>
                <span class="sort-category-label">${group.label}</span>
                <div class="sort-category-arrows">
                    <button class="sort-category-arrow" ${index === 0 ? 'disabled' : ''}
                            onclick="window.bankModal.moveCategoryUp('${groupId}')">\u25B2</button>
                    <button class="sort-category-arrow" ${index === categoryOrder.length - 1 ? 'disabled' : ''}
                            onclick="window.bankModal.moveCategoryDown('${groupId}')">\u25BC</button>
                </div>
            </div>
        `;
    }).join('');

    return `
        <div class="sort-settings-panel">
            <div class="sort-settings-header">
                <span>Sort Settings</span>
                <button class="sort-settings-close" onclick="window.bankModal.toggleSortSettings()">&times;</button>
            </div>
            <div class="sort-settings-body">
                <div class="sort-settings-section">
                    <div class="sort-settings-section-title">Sort Directions</div>
                    ${directionHTML}
                </div>
                <div class="sort-settings-section">
                    <div class="sort-settings-section-title">Category Order</div>
                    <div class="sort-category-list" id="sort-category-list">
                        ${categoryHTML}
                    </div>
                </div>
                <button class="sort-reset-btn" onclick="window.bankModal.resetSortSettings()">Reset Defaults</button>
            </div>
        </div>
    `;
}

/**
 * Toggle the sort settings panel open/closed
 */
export function toggleSortSettings() {
    sortSettingsOpen = !sortSettingsOpen;
    if (!modalElement) return;

    const existing = modalElement.querySelector('.sort-settings-panel');
    if (existing) {
        existing.remove();
        sortSettingsOpen = false;
        return;
    }

    sortSettingsOpen = true;
    const modalBody = modalElement.querySelector('.modal-body');
    if (!modalBody) return;

    modalBody.insertAdjacentHTML('beforeend', renderSortSettingsPanel());
    initCategoryDragDrop();
}

/**
 * Toggle a sort direction (name, value, quantity)
 */
export function toggleSortDirection(key, dir) {
    sortDirections[key] = dir;
    localStorage.setItem(SORT_DIR_KEY, JSON.stringify(sortDirections));
    refreshSettingsPanel();
    refreshSortDropdowns();
}

/**
 * Move a category group up in the order
 */
export function moveCategoryUp(groupId) {
    const idx = categoryOrder.indexOf(groupId);
    if (idx <= 0) return;
    [categoryOrder[idx - 1], categoryOrder[idx]] = [categoryOrder[idx], categoryOrder[idx - 1]];
    localStorage.setItem(CAT_ORDER_KEY, JSON.stringify(categoryOrder));
    refreshSettingsPanel();
}

/**
 * Move a category group down in the order
 */
export function moveCategoryDown(groupId) {
    const idx = categoryOrder.indexOf(groupId);
    if (idx < 0 || idx >= categoryOrder.length - 1) return;
    [categoryOrder[idx], categoryOrder[idx + 1]] = [categoryOrder[idx + 1], categoryOrder[idx]];
    localStorage.setItem(CAT_ORDER_KEY, JSON.stringify(categoryOrder));
    refreshSettingsPanel();
}

/**
 * Reset sort settings to defaults
 */
export function resetSortSettings() {
    sortDirections = { ...DEFAULT_DIRECTIONS };
    categoryOrder = [...DEFAULT_CATEGORY_ORDER];
    localStorage.setItem(SORT_DIR_KEY, JSON.stringify(sortDirections));
    localStorage.setItem(CAT_ORDER_KEY, JSON.stringify(categoryOrder));
    refreshSettingsPanel();
    refreshSortDropdowns();
}

/**
 * Refresh the settings panel content in place
 */
function refreshSettingsPanel() {
    const existing = modalElement?.querySelector('.sort-settings-panel');
    if (!existing) return;
    const modalBody = modalElement.querySelector('.modal-body');
    if (!modalBody) return;
    existing.remove();
    modalBody.insertAdjacentHTML('beforeend', renderSortSettingsPanel());
    initCategoryDragDrop();
}

/**
 * Refresh all sort dropdown labels to reflect current directions
 */
function refreshSortDropdowns() {
    if (!modalElement) return;

    const sortOptions = getSortOptions();
    const selects = modalElement.querySelectorAll('.bank-sort-select');
    selects.forEach(select => {
        // Update option labels
        const options = select.querySelectorAll('option');
        options.forEach(opt => {
            if (!opt.disabled) {
                const sortOpt = sortOptions.find(so => so.value === opt.value);
                if (sortOpt) opt.textContent = sortOpt.label;
            }
        });

        // Update placeholder text to match current sort
        const placeholder = select.querySelector('option[disabled]');
        if (placeholder) {
            const handler = select.getAttribute('onchange');
            let currentSort = 'default';
            if (handler?.includes('setBankSort')) currentSort = bankSort;
            else if (handler?.includes('setBloomholdSort')) currentSort = bloomholdSort;
            else if (handler?.includes('setInventorySort')) currentSort = inventorySort;
            placeholder.textContent = getSortLabel(currentSort);
        }
    });
}

/**
 * Initialize drag and drop for category list reordering
 */
function initCategoryDragDrop() {
    const list = modalElement?.querySelector('#sort-category-list');
    if (!list) return;

    let draggedGroupId = null;

    list.addEventListener('dragstart', (e) => {
        const row = e.target.closest('.sort-category-row');
        if (!row) return;
        draggedGroupId = row.dataset.groupId;
        row.classList.add('dragging');
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', draggedGroupId);
    });

    list.addEventListener('dragover', (e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        const row = e.target.closest('.sort-category-row');
        if (row && row.dataset.groupId !== draggedGroupId) {
            // Remove drag-over from all rows
            list.querySelectorAll('.sort-category-row').forEach(r => r.classList.remove('drag-over'));
            row.classList.add('drag-over');
        }
    });

    list.addEventListener('dragleave', (e) => {
        const row = e.target.closest('.sort-category-row');
        if (row) row.classList.remove('drag-over');
    });

    list.addEventListener('drop', (e) => {
        e.preventDefault();
        const targetRow = e.target.closest('.sort-category-row');
        if (!targetRow || !draggedGroupId) return;

        const targetGroupId = targetRow.dataset.groupId;
        if (targetGroupId === draggedGroupId) return;

        // Reorder: remove dragged from current position, insert at target position
        const fromIdx = categoryOrder.indexOf(draggedGroupId);
        const toIdx = categoryOrder.indexOf(targetGroupId);
        if (fromIdx < 0 || toIdx < 0) return;

        categoryOrder.splice(fromIdx, 1);
        categoryOrder.splice(toIdx, 0, draggedGroupId);
        localStorage.setItem(CAT_ORDER_KEY, JSON.stringify(categoryOrder));

        refreshSettingsPanel();
    });

    list.addEventListener('dragend', (e) => {
        draggedGroupId = null;
        list.querySelectorAll('.sort-category-row').forEach(r => {
            r.classList.remove('dragging', 'drag-over');
        });
    });
}

// Expose to window for onclick handlers
window.bankModal = {
    open: openBankModal,
    close: closeBankModal,
    handleSlotClick,
    depositAll: handleDepositAll,
    moveToBloomhold: handleMoveToBloomhold,
    purchaseBloomhold,
    setInventoryFilter,
    setBankFilter,
    setBloomholdFilter,
    toggleAutoDeposit,
    switchTab,
    setQuantity,
    setCustomQuantity,
    setBankSort,
    setBloomholdSort,
    setInventorySort,
    toggleSortSettings,
    toggleSortDirection,
    moveCategoryUp,
    moveCategoryDown,
    resetSortSettings,
};

export default {
    openBankModal,
    closeBankModal,
    handleSlotClick,
    handleDepositAll,
    handleMoveToBloomhold,
    purchaseBloomhold,
    setInventoryFilter,
    setBankFilter,
    setBloomholdFilter,
    toggleAutoDeposit,
    switchTab,
};
